/**
 * gs-filter-slider — curseur double-poignée maison (Green Society), sans app.
 *
 * Deux modes :
 *   - "price"     : se greffe sur les inputs min/max natifs du filtre Prix d'Horizon
 *     (price-facet-component) ; au relâchement, déclenche updatePriceFilterAndResults.
 *   - "clientcbd" : filtre le Taux de CBD CÔTÉ NAVIGATEUR (masque les cartes produit
 *     selon leur metafield cbd_pct, lu sur data-gs-cbd des <li> de la grille). 100 %
 *     indépendant du moteur de facettes Shopify → curseur réellement libre (n'importe
 *     quel %), qui reste exactement où l'utilisateur le met, sans se battre avec les
 *     rafraîchissements AJAX. Pensé pour de petites collections (pas de pagination).
 */
class GsFilterSlider extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode || 'price';
    this.floor = parseFloat(this.dataset.floor || '0');
    this.ceil = parseFloat(this.dataset.ceil || '100');
    if (!(this.ceil > this.floor)) this.ceil = this.floor + 1;

    this.minR = this.querySelector('[data-handle="min"]');
    this.maxR = this.querySelector('[data-handle="max"]');
    this.fill = this.querySelector('[data-fill]');
    this.readout = this.querySelector('[data-readout]');
    if (!this.minR || !this.maxR) return;

    if (this.mode === 'clientcbd') {
      this.#initClient();
      return;
    }

    this.#initPrice();
    this.minR.addEventListener('input', this.#onSlide);
    this.maxR.addEventListener('input', this.#onSlide);
    this.minR.addEventListener('change', this.#commit);
    this.maxR.addEventListener('change', this.#commit);
    this.#paint();
  }

  disconnectedCallback() {
    if (this._gridObs) this._gridObs.disconnect();
    window.removeEventListener('gs-cbd-clear', this.#onExternalClear);
  }

  #clamp(v) {
    if (isNaN(v)) return this.floor;
    return Math.min(this.ceil, Math.max(this.floor, v));
  }

  #read(input, fallback) {
    if (!input || input.value === '' || input.value == null) return fallback;
    return this.#clamp(parseFloat(input.value));
  }

  /* ===================== persistance (par collection) ===================== */
  #storeKey() {
    return 'gs-cbd:' + location.pathname;
  }
  #storeRange(r) {
    try {
      if (r) sessionStorage.setItem(this.#storeKey(), r[0] + ',' + r[1]);
      else sessionStorage.removeItem(this.#storeKey());
    } catch (e) {
      /* sessionStorage indisponible : on continue sans persistance */
    }
  }
  #loadRange() {
    try {
      const s = sessionStorage.getItem(this.#storeKey());
      if (!s) return null;
      const p = s.split(',').map(Number);
      return p.length === 2 && !isNaN(p[0]) && !isNaN(p[1]) ? p : null;
    } catch (e) {
      return null;
    }
  }

  /* ===================== mode CLIENT (taux CBD) ===================== */
  #initClient() {
    this._range = this.#loadRange();
    this.minR.addEventListener('input', this.#onClientSlide);
    this.maxR.addEventListener('input', this.#onClientSlide);
    this.minR.addEventListener('change', this.#onClientSlide);
    this.maxR.addEventListener('change', this.#onClientSlide);

    this.#applyClient();

    // Ré-applique le masquage quand la grille est re-rendue (autres filtres en AJAX).
    // On observe un conteneur stable (results-list) ; on ignore nos propres écritures
    // (compteur, readout, curseur) pour ne pas boucler — y compris entre les deux
    // instances du curseur (barre latérale + tiroir mobile).
    this._resultsNode =
      document.querySelector('results-list') ||
      document.getElementById('ResultsList') ||
      document.querySelector('.product-grid') ||
      document.body;
    this._gridObs = new MutationObserver(this.#onGridMutation);
    this._gridObs.observe(this._resultsNode, { childList: true, subtree: true });

    window.addEventListener('gs-cbd-clear', this.#onExternalClear);
    GsFilterSlider._installClearHook();
  }

  #onGridMutation = (records) => {
    for (const r of records) {
      const t = r.target;
      if (t && t.closest && t.closest('.products-count-wrapper, .gs-rng__readout, .gs-cbd-slider')) {
        continue; // nos propres mises à jour (compteur / curseur) → ignorées
      }
      this.#applyClient(); // vrai re-rendu de la grille → on ré-applique le masquage
      return;
    }
  };

  #onClientSlide = (event) => {
    event.stopPropagation(); // ne JAMAIS déclencher le formulaire de facettes Horizon
    let lo = parseFloat(this.minR.value);
    let hi = parseFloat(this.maxR.value);
    if (lo > hi) {
      if (event.target === this.minR) {
        hi = lo;
        this.maxR.value = String(hi);
      } else {
        lo = hi;
        this.minR.value = String(lo);
      }
    }
    this.minR.style.zIndex = event.target === this.minR ? '5' : '4';
    const full = lo <= this.floor && hi >= this.ceil;
    this._range = full ? null : [lo, hi];
    this.#storeRange(this._range);
    this.#applyClient();
  };

  #onExternalClear = () => {
    this._range = null;
    this.#storeRange(null);
    this.#applyClient();
  };

  // Cœur : masque/affiche les cartes selon leur taux, met à jour readout + compteur.
  #applyClient = () => {
    const stored = this._range || this.#loadRange();
    if (stored) {
      this._range = stored;
      this.minR.value = String(this.#clamp(stored[0]));
      this.maxR.value = String(this.#clamp(stored[1]));
    } else {
      this.minR.value = String(this.floor);
      this.maxR.value = String(this.ceil);
    }
    const lo = parseFloat(this.minR.value);
    const hi = parseFloat(this.maxR.value);
    const full = lo <= this.floor && hi >= this.ceil;

    let visible = 0;
    let hasData = false;
    const items = document.querySelectorAll('.product-grid__item[data-gs-cbd]');
    items.forEach((li) => {
      const raw = li.getAttribute('data-gs-cbd');
      const v = raw === '' || raw == null ? NaN : parseFloat(raw);
      if (!isNaN(v)) hasData = true;
      // Produit sans taux renseigné : toujours visible (le filtre CBD ne le concerne pas).
      const show = full || isNaN(v) ? true : v >= lo && v <= hi;
      li.style.display = show ? '' : 'none';
      if (show) visible++;
    });

    this.#paintClient(lo, hi, full);
    if (hasData) this.#updateCount(visible);
  };

  #paintClient = (lo, hi, full) => {
    const span = this.ceil - this.floor || 1;
    const l = Math.max(0, Math.min(100, ((lo - this.floor) / span) * 100));
    const r = Math.max(0, Math.min(100, ((hi - this.floor) / span) * 100));
    if (this.fill) {
      this.fill.style.insetInlineStart = l + '%';
      this.fill.style.insetInlineEnd = 100 - r + '%';
    }
    if (this.readout) {
      this.readout.textContent = full ? 'Tous les taux' : Math.round(lo) + ' % – ' + Math.round(hi) + ' %';
    }
  };

  #updateCount(n) {
    document.querySelectorAll('.products-count-wrapper span').forEach((s) => {
      s.textContent = n > 1 ? n + ' articles' : n + ' article';
    });
  }

  // Hook global (une fois) : un clic sur « Tout effacer » réinitialise le curseur.
  static _installClearHook() {
    if (window.__gsCbdClearHook) return;
    window.__gsCbdClearHook = true;
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (t && t.closest && t.closest('.facets__clear-all')) {
          window.dispatchEvent(new CustomEvent('gs-cbd-clear'));
        }
      },
      true
    );
  }

  /* ===================== mode PRIX (inchangé) ===================== */
  #initPrice() {
    this.minInput = this.dataset.minId ? document.getElementById(this.dataset.minId) : null;
    this.maxInput = this.dataset.maxId ? document.getElementById(this.dataset.maxId) : null;
    this.minR.value = String(this.#read(this.minInput, this.floor));
    this.maxR.value = String(this.#read(this.maxInput, this.ceil));
    if (this.minInput) this.minInput.addEventListener('input', this.#syncFromPrice);
    if (this.maxInput) this.maxInput.addEventListener('input', this.#syncFromPrice);
  }

  #syncFromPrice = () => {
    this.minR.value = String(this.#read(this.minInput, this.floor));
    this.maxR.value = String(this.#read(this.maxInput, this.ceil));
    this.#paint();
  };

  #onSlide = (event) => {
    let lo = parseFloat(this.minR.value);
    let hi = parseFloat(this.maxR.value);
    if (lo > hi) {
      if (event.target === this.minR) {
        hi = lo;
        this.maxR.value = String(hi);
      } else {
        lo = hi;
        this.minR.value = String(lo);
      }
    }
    this.minR.style.zIndex = event.target === this.minR ? '5' : '4';
    if (this.minInput) this.minInput.value = lo <= this.floor ? '' : String(lo);
    if (this.maxInput) this.maxInput.value = hi >= this.ceil ? '' : String(hi);
    this.#paint();
  };

  #commit = () => {
    const comp = this.closest('price-facet-component');
    if (comp && typeof comp.updatePriceFilterAndResults === 'function') {
      comp.updatePriceFilterAndResults();
      return;
    }
    const target = this.maxInput || this.minInput;
    if (target) target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #paint = () => {
    const lo = parseFloat(this.minR.value);
    const hi = parseFloat(this.maxR.value);
    const span = this.ceil - this.floor || 1;
    const l = Math.max(0, Math.min(100, ((lo - this.floor) / span) * 100));
    const r = Math.max(0, Math.min(100, ((hi - this.floor) / span) * 100));
    if (this.fill) {
      this.fill.style.insetInlineStart = l + '%';
      this.fill.style.insetInlineEnd = 100 - r + '%';
    }
    if (this.readout) {
      const full = lo <= this.floor && hi >= this.ceil;
      this.readout.textContent = full ? 'Tous les taux' : Math.round(lo) + ' % – ' + Math.round(hi) + ' %';
    }
  };
}

if (!customElements.get('gs-filter-slider')) {
  customElements.define('gs-filter-slider', GsFilterSlider);
}
